/**
 * La semana, tal y como la entiende la aplicación.
 *
 * Los días se numeran como `Date.getDay()` —0 domingo … 6 sábado— porque es
 * lo que guarda la API en `dayOfWeek`. Pero se **enseñan** empezando por el
 * lunes, que es donde empieza una semana de gimnasio: quien entrena de lunes
 * a viernes espera ver esos cinco días juntos, no partidos entre dos semanas.
 */

/** Índice = `dayOfWeek` de la API. */
export const NOMBRE_DEL_DIA = [
  'Domingo',
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
] as const;

/** Una letra por día, para la tira de la semana. */
export const INICIAL_DEL_DIA = ['D', 'L', 'M', 'X', 'J', 'V', 'S'] as const;

/** El orden en el que se pintan: lunes primero, domingo el último. */
export const ORDEN_DE_LA_SEMANA = [1, 2, 3, 4, 5, 6, 0] as const;

/**
 * El día siguiente **en el orden en el que se enseña la semana**.
 *
 * No es `dia + 1`: la semana se pinta de lunes a domingo, así que después del
 * sábado (6) viene el domingo (0) y después del domingo, el lunes (1).
 */
export function diaSiguiente(dia: number): number {
  const i = ORDEN_DE_LA_SEMANA.indexOf(dia as (typeof ORDEN_DE_LA_SEMANA)[number]);
  return ORDEN_DE_LA_SEMANA[(i + 1) % ORDEN_DE_LA_SEMANA.length];
}

/** El día anterior en ese mismo orden. */
export function diaAnterior(dia: number): number {
  const i = ORDEN_DE_LA_SEMANA.indexOf(dia as (typeof ORDEN_DE_LA_SEMANA)[number]);
  const largo = ORDEN_DE_LA_SEMANA.length;
  return ORDEN_DE_LA_SEMANA[(i - 1 + largo) % largo];
}

/** Qué día de la semana es hoy (0 domingo … 6 sábado). */
export function diaDeHoy(ahora: Date = new Date()): number {
  return ahora.getDay();
}

/** Fecha local como YYYY-MM-DD, sin pasar por UTC. */
export function comoFecha(d: Date): string {
  const mes = `${d.getMonth() + 1}`.padStart(2, '0');
  const dia = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

/**
 * La fecha en la que cae un día dentro de la semana en curso.
 *
 * Se usa solo para enseñarla («martes 24»): la rutina se pide por día, no por
 * fecha. Lo que sí lleva fecha de verdad es lo que entrena cada socio.
 */
export function fechaDelDia(dia: number, hoy: Date = new Date()): string {
  const desdeElLunes = diaDeHoy(hoy) === 0 ? 6 : diaDeHoy(hoy) - 1;
  const lunes = new Date(hoy);
  lunes.setDate(hoy.getDate() - desdeElLunes);
  const objetivo = new Date(lunes);
  objetivo.setDate(lunes.getDate() + (dia === 0 ? 6 : dia - 1));
  return comoFecha(objetivo);
}

/** El día del mes de ese día de la semana, para la etiqueta de la tira. */
export function numeroDelDia(dia: number, hoy: Date = new Date()): number {
  return Number(fechaDelDia(dia, hoy).slice(-2));
}
